import { $, pageData } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';
import { fillForm } from '../lib/form.js';
import { mountResourceEditor } from '../lib/resource-editor.js';
import { findSelectMenu } from '../lib/select-menu.js';
import { toastError } from '../lib/toast.js';

/**
 * The editor of one watch.
 *
 * The preview button runs the rule being written over the days the server
 * actually had. That is the point of the page: a threshold nobody has tested
 * either sends nothing for a month or sends forty alerts on its first night,
 * and both failures are only visible afterwards.
 */
interface Watch {
  _id: string;
  name: string;
  metric: string;
  windowMinutes: number;
  condition: string;
  threshold: number;
  severity: string;
  cooldownMinutes: number;
  channelId: string;
  enabled: boolean;
}

interface Preview {
  minutes: number;
  measured: number;
  hits: { at: string; value: number; baseline: number | null }[];
}

export function initWatchEditorPage(): void {
  const form = $<HTMLFormElement>('[data-watch-form]');
  if (!form) return;

  const { watch, guildId } = pageData<{ watch: Watch; guildId: string }>();

  fillForm(form, { ...watch });

  choose(form, 'metric', watch.metric || 'messages');
  choose(form, 'condition', watch.condition || 'above');
  choose(form, 'severity', watch.severity || 'warning');
  choose(form, 'channelId', watch.channelId);

  // Ticked by default on a creation: a watch somebody has just described is
  // meant to watch. `fillForm` cannot guess it — the document does not exist.
  const enabled = form.elements.namedItem('enabled');
  if (enabled instanceof HTMLInputElement) enabled.checked = watch._id ? watch.enabled : true;

  mountResourceEditor({
    endpoint: 'watch',
    listUrl: `/modules/watch?guildId=${guildId}`,
    guildId,
    form,
    itemId: watch._id,
    serialize: () => serialize(form),

    labels: {
      saved: ct('watchSaved', 'Watch saved.'),
      confirmDelete: ct('watchDeleteConfirm', 'Remove this watch?'),
      deleted: ct('watchDeleted', 'Watch removed.'),
    },
  });

  $<HTMLButtonElement>('[data-watch-preview]')?.addEventListener('click', () => {
    void preview(form, guildId);
  });
}

function serialize(form: HTMLFormElement): Record<string, unknown> {
  return {
    name: text(form, 'name'),
    metric: findSelectMenu(form, 'metric')?.single ?? 'messages',
    windowMinutes: number(form, 'windowMinutes', 10),
    condition: findSelectMenu(form, 'condition')?.single ?? 'above',
    threshold: number(form, 'threshold', 20),
    severity: findSelectMenu(form, 'severity')?.single ?? 'warning',
    cooldownMinutes: number(form, 'cooldownMinutes', 60),
    channelId: findSelectMenu(form, 'channelId')?.single ?? '',
    enabled: checked(form, 'enabled'),
    publish: false,
  };
}

async function preview(form: HTMLFormElement, guildId: string): Promise<void> {
  const target = $<HTMLElement>('[data-watch-preview-result]');
  if (!target) return;

  target.textContent = ct('watchPreviewRunning', 'Reading the server’s history…');

  try {
    const result = await api.post<Preview & { ok: true }>(`/api/watch/preview?guildId=${guildId}`, {
      ...serialize(form),
      guildId,
    });

    target.replaceChildren(...lines(result));
  } catch (error) {
    target.textContent = '';
    toastError(error);
  }
}

/**
 * The preview, in sentences.
 *
 * "Nothing" is said two different ways on purpose: a rule that never fires over
 * a week of real traffic is a rule to lower, and a rule that never fires over a
 * week with nothing in it says nothing at all about the rule.
 */
function lines(result: Preview): HTMLElement[] {
  const hours = Math.round(result.minutes / 60);

  if (result.measured === 0) {
    return [text_(ct('watchPreviewNoHistory', 'No history yet: measuring starts now.'))];
  }

  const summary = text_(
    result.hits.length === 0
      ? ct('watchPreviewSilent', 'Would not have fired in the last {hours} h.', {
          hours: String(hours),
        })
      : ct('watchPreviewFired', 'Would have fired {count} times in the last {hours} h.', {
          count: String(result.hits.length),
          hours: String(hours),
        }),
  );

  const when = result.hits.slice(0, 8).map((hit) => {
    const at = new Date(hit.at).toLocaleString();
    const usual =
      hit.baseline === null
        ? ''
        : ` · ${ct('watchPreviewUsual', 'usual {usual}', { usual: String(Math.round(hit.baseline)) })}`;

    return text_(`${at} · ${String(Math.round(hit.value))}${usual}`, 'text-subtle');
  });

  return [summary, ...when];
}

function text_(content: string, className = 'font-medium'): HTMLElement {
  const node = document.createElement('p');
  node.className = className;
  node.textContent = content;
  return node;
}

function choose(form: HTMLFormElement, name: string, selected: string): void {
  const menu = findSelectMenu(form, name);
  if (menu) menu.value = selected ? [selected] : [];
}

function text(form: HTMLFormElement, name: string): string {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement ? field.value.trim() : '';
}

function number(form: HTMLFormElement, name: string, fallback: number): number {
  const field = form.elements.namedItem(name);
  const value = field instanceof HTMLInputElement ? Number(field.value) : NaN;
  return Number.isFinite(value) ? value : fallback;
}

function checked(form: HTMLFormElement, name: string): boolean {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement ? field.checked : false;
}
